'use client';

import { usePathname } from 'next/navigation';
import { useRef } from 'react';

import PlanDetailsModal from './PlanDetailsModal';
import PlanMapModal from './PlanMapModal';
import PlanQuoteModal from './PlanQuoteModal';

const PLAN_PATH_RE = /^\/freight\/plan\/([^/]+)\/([^/]+)(?:\/(map|quote))?\/?$/;

/**
 * Renders the freight plan modals (details/map/quote) on top of whatever page is mounted.
 *
 * Rendered by every root layout in place of the old @modal parallel-route slot. The old
 * setup used route interception, but three route groups intercepting the same URL is
 * ambiguous - Next.js always resolved the (payload) tree's interceptor, so the modals only
 * worked from CMS pages. Instead, PlanModalLink pushes the plan URL with
 * window.history.pushState (no real navigation, the underlying page stays mounted) and this
 * host opens the matching modal off the pathname.
 *
 * The full plan pages live at the same paths (hard loads, shared links), so a modal only
 * opens when the pathname CHANGED to a plan URL client-side - never for the URL the app
 * loaded on.
 */
export default function PlanModalHost() {
    const pathname = usePathname();
    const initialPathname = useRef(pathname);

    if (pathname === initialPathname.current) {
        return null;
    }

    const match = pathname.match(PLAN_PATH_RE);
    if (!match) {
        return null;
    }

    const [, resultId, planId, sub] = match;

    if (sub === 'map') {
        return <PlanMapModal resultId={resultId} planId={planId} />;
    }
    if (sub === 'quote') {
        return <PlanQuoteModal resultId={resultId} planId={planId} />;
    }
    return <PlanDetailsModal resultId={resultId} planId={planId} />;
}
